import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/contexts/ToastContext'
import { invitesApi } from '@/lib/invitesApi'
import { supabase } from '@/lib/supabase'
import seacureLogo from '@/assets/SEACURE(CYBER SECURITY).png'

export const AcceptInvitePage: React.FC = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const token = params.get('token') ?? ''

  const [checking, setChecking] = useState(true)
  const [valid, setValid] = useState(false)
  const [invitedEmail, setInvitedEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      setValid(false)
      setChecking(false)
      return
    }
    invitesApi.checkToken(token)
      .then(({ valid, email }) => {
        setValid(valid)
        if (email) setInvitedEmail(email)
      })
      .catch(() => setValid(false))
      .finally(() => setChecking(false))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError("Passwords don't match.")
      return
    }
    setSubmitting(true)
    try {
      const { error: acceptError } = await supabase.auth.acceptInvite({ token, password })
      if (acceptError) {
        setError(acceptError.message ?? 'Failed to accept invite.')
        return
      }
      setDone(true)
      success(`Welcome to SEACURE DB, ${invitedEmail}`)
      // Tokens are set; AuthContext picks up via seacuredb:auth event.
      setTimeout(() => navigate('/dashboard', { replace: true }), 800)
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to accept invite.'
      setError(msg)
      showError('Activation failed', msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-blue-600 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-slate-600">Checking your invitation…</p>
        </div>
      </div>
    )
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="text-center py-12">
            <AlertTriangle className="h-16 w-16 text-amber-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Invitation invalid or expired</h2>
            <p className="text-sm text-slate-600 mb-6">
              This invite link is no longer valid. Contact your administrator for a new one.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={seacureLogo} alt="SEACURE" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900">SEACURE DB</h1>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-center gap-2">
              <Lock className="h-5 w-5" />
              {done ? 'Account activated' : 'Welcome'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="text-center space-y-3">
                <CheckCircle className="h-16 w-16 text-emerald-600 mx-auto" />
                <p className="text-sm text-slate-700">Signing you in…</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-slate-600">
                  Set a password to activate your account for <strong>{invitedEmail}</strong>.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-2">New password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm password</label>
                  <Input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    required
                  />
                </div>
                {error && (
                  <div className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</div>
                )}
                <Button
                  type="submit"
                  className="w-full bg-[#e60000] hover:bg-[#cc0000] text-white"
                  disabled={submitting || !password || password !== confirm}
                >
                  {submitting ? 'Activating…' : 'Activate account'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
